import { formatNumberWithSpaces } from "@/composables/formatUtils";
import type { FlattenedPortfolioItem } from "@/types";
import type { TranslationFn } from "@/utils/countries";

/**
 * Checks if a portfolio item is available for selection/transfer
 *
 * @param item - The portfolio item to check
 * @returns True if the item has status "AVAILABLE"
 */
export function isItemAvailable(item: FlattenedPortfolioItem): boolean {
  return item.status === "AVAILABLE";
}

export function isItemWaitingForSale(item: FlattenedPortfolioItem): boolean {
  return item.status === "WAITING_FOR_SALE";
}

/** Sum the MWh `count` across the AVAILABLE items in the set (others don't contribute). */
export function sumAvailableMwh(items: FlattenedPortfolioItem[]): number {
  return items.reduce(
    (total, item) => (isItemAvailable(item) ? total + item.count : total),
    0
  );
}

/**
 * Selection policy that accepts any item. Use when the caller has already
 * restricted the visible set (e.g. via the table's `items` prop) or for
 * admin flows that should not be gated by status.
 */
export const allItemsSelectable = (_item: FlattenedPortfolioItem): boolean =>
  true;

/**
 * The order an auto-select takes rows in: oldest production month first, so the oldest certificates
 * leave inventory before they expire, and smaller rows first within a month. Rows without a month go last.
 */
export const compareOldestFirst = (
  a: { production_month: string | null; count: number },
  b: { production_month: string | null; count: number }
): number => {
  const monthA = a.production_month ?? "9999-99";
  const monthB = b.production_month ?? "9999-99";
  return monthA !== monthB ? monthA.localeCompare(monthB) : a.count - b.count;
};

/**
 * Whether `selected` MWh takes only part of the `max` a row allows: the one definition of a partial
 * row, shared by the selection (its `?portItemIds=` encoding and partial count) and the count cell.
 */
export const isPartialCount = (selected: number, max: number): boolean =>
  selected < max;

/**
 * What a Count cell shows outside its amount editor.
 *
 * @property amount - The cell's number: the selected amount, else what can be selected
 * @property of - The row's whole amount for the "of {count}" line; null when `amount` is all of it
 * @property partial - Only part of what can be selected is selected
 */
export interface CountDisplay {
  amount: number;
  of: number | null;
  partial: boolean;
}

/**
 * The one decision of what a Count cell shows, shared by the table's plain cells and the selected
 * rows' PortfolioCountCell.
 *
 * @param amounts.count - The row's whole amount
 * @param amounts.max - The most of the row that can be selected, at most `count`
 * @param amounts.selected - The amount selected from the row, or the share a read-only context covers;
 *   undefined when the row is not selected
 */
export const countDisplay = ({
  count,
  max,
  selected,
}: {
  count: number;
  max: number;
  selected?: number;
}): CountDisplay => {
  const amount = selected ?? max;
  return {
    amount,
    of: amount < count ? count : null,
    partial: selected !== undefined && isPartialCount(selected, max),
  };
};

/**
 * The Count cell tooltip of a row the page lets select less of than its count, while the cell shows
 * that cap rather than a partial selection; undefined otherwise.
 */
export const cappedCountTitle = (
  { amount, of, partial }: CountDisplay,
  t: TranslationFn
): string | undefined =>
  !partial && of !== null
    ? t("dashboard.portfolio.table.count_cell.capped_title", {
        max: formatNumberWithSpaces(amount),
        count: formatNumberWithSpaces(of),
      })
    : undefined;
